// Documents Module - Redesigned with AI Generation
import React, { useState } from 'react';
import SectionAgent from './SectionAgent';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Download, Sparkles, X,
  Copy, Check, Mic, MicOff, Clock, Eye
} from 'lucide-react';
import AIAgentModule from './AIAgentModule';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DOC_TYPES = [
  { id: 'cv', label: 'Currículum Vitae', emoji: '👔', description: 'CV profesional completo', fields: ['name', 'profession', 'experience', 'skills', 'education'] },
  { id: 'contract', label: 'Contrato Simple', emoji: '📜', description: 'Contrato entre partes', fields: ['party1', 'party2', 'purpose', 'amount', 'duration'] },
  { id: 'letter', label: 'Carta Formal', emoji: '✉️', description: 'Carta para cualquier propósito', fields: ['recipient', 'purpose', 'details', 'sender'] },
  { id: 'report', label: 'Informe / Reporte', emoji: '📊', description: 'Informe profesional', fields: ['title', 'author', 'topic', 'period', 'summary'] },
  { id: 'invoice', label: 'Factura / Recibo', emoji: '🧾', description: 'Factura de servicio', fields: ['business', 'client', 'services', 'amount', 'date'] },
  { id: 'proposal', label: 'Propuesta Comercial', emoji: '💼', description: 'Propuesta de negocio', fields: ['company', 'client', 'service', 'benefits', 'price'] },
  { id: 'nda', label: 'NDA / Confidencialidad', emoji: '🔒', description: 'Acuerdo de confidencialidad', fields: ['party1', 'party2', 'information', 'duration'] },
  { id: 'complaint', label: 'Carta de Queja', emoji: '📢', description: 'Queja formal', fields: ['sender', 'recipient', 'complaint', 'resolution'] },
  { id: 'recommendation', label: 'Carta de Recomendación', emoji: '⭐', description: 'Recomendación profesional', fields: ['recommender', 'candidate', 'role', 'qualities', 'duration'] },
  { id: 'rental', label: 'Contrato de Arrendamiento', emoji: '🏠', description: 'Contrato de renta', fields: ['landlord', 'tenant', 'property', 'rent', 'duration'] },
  { id: 'custom', label: 'Documento Personalizado', emoji: '✍️', description: 'Escribe lo que necesitas', fields: ['title', 'description', 'content'] },
];

const FIELD_LABELS = {
  name: 'Nombre completo', profession: 'Profesión / Puesto', experience: 'Experiencia laboral',
  skills: 'Habilidades principales', education: 'Educación / Formación',
  party1: 'Primera parte', party2: 'Segunda parte', purpose: 'Propósito / Objetivo',
  amount: 'Monto ($)', duration: 'Duración / Vigencia', recipient: 'Destinatario',
  details: 'Detalles / Contenido', sender: 'Tu nombre / Remitente',
  title: 'Título del documento', author: 'Autor / Responsable', topic: 'Tema principal',
  period: 'Período reportado', summary: 'Resumen ejecutivo', business: 'Empresa / Negocio',
  client: 'Cliente', services: 'Servicios prestados', date: 'Fecha',
  company: 'Tu empresa', service: 'Servicio / Producto', benefits: 'Beneficios clave',
  price: 'Precio propuesto', information: 'Información confidencial',
  complaint: 'Descripción de la queja', resolution: 'Resolución solicitada',
  recommender: 'Nombre del recomendante', candidate: 'Nombre del candidato',
  role: 'Cargo / Función', qualities: 'Cualidades destacadas',
  landlord: 'Arrendador', tenant: 'Arrendatario', property: 'Dirección del inmueble',
  rent: 'Renta mensual ($)', description: 'Descripción', content: 'Contenido principal',
};

const MULTILINE_FIELDS = ['experience', 'details', 'skills', 'content', 'description', 'complaint', 'services', 'powers', 'qualities', 'benefits', 'information'];

const DocumentsModule = ({ user }) => {
  const [selectedType, setSelectedType] = useState(null);
  const [fields, setFields] = useState({});
  const [generatedDoc, setGeneratedDoc] = useState('');
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [listening, setListening] = useState('');
  const [history, setHistory] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  const handleVoice = (fieldId) => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Voz no disponible'); return; }
    const r = new SR();
    r.lang = 'es-MX';
    r.onresult = (e) => { setFields(p => ({ ...p, [fieldId]: e.results[0][0].transcript })); setListening(''); };
    r.onerror = r.onend = () => setListening('');
    r.start();
    setListening(fieldId);
  };

  const generateDocument = async () => {
    if (!selectedType) { toast.error('Selecciona un tipo de documento'); return; }
    const emptyFields = selectedType.fields.filter(f => !fields[f]?.trim());
    if (emptyFields.length > 0) { toast.error('Completa todos los campos'); return; }
    setGenerating(true);
    try {
      const prompt = `Genera un ${selectedType.label} profesional en español con los siguientes datos:\n${
        selectedType.fields.map(f => `${FIELD_LABELS[f] || f}: ${fields[f]}`).join('\n')
      }\n\nEl documento debe ser formal, bien estructurado y listo para usar. Fecha: ${new Date().toLocaleDateString('es-MX')}.`;

      const res = await axios.post(`${API}/chat`, {
        message: prompt,
        conversation_id: `doc_${Date.now()}`,
        user_id: user.id,
        model: 'openai/gpt-4o-mini'
      });
      const doc = res.data.response || res.data.message || 'Error al generar';
      setGeneratedDoc(doc);
      setShowPreview(true);
      setHistory(prev => [{ type: selectedType.label, emoji: selectedType.emoji, doc, fields: { ...fields }, date: new Date().toISOString() }, ...prev.slice(0, 9)]);
      toast.success('Documento generado');
    } catch { toast.error('Error al generar documento'); }
    finally { setGenerating(false); }
  };

  const copyDoc = () => {
    navigator.clipboard.writeText(generatedDoc);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copiado');
  };

  const downloadDoc = () => {
    const blob = new Blob([generatedDoc], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `${selectedType?.label || 'documento'}-${Date.now()}.txt`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Descargado');
  };

  return (
      <>
      <SectionAgent section="documents" />
      <div className="p-4 md:p-6 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileText className="text-orange-400" size={24} />Documentos IA
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Genera documentos profesionales con inteligencia artificial</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          {/* Type grid */}
          <div className="glass-panel p-5 rounded-2xl">
            <h3 className="text-white font-semibold mb-4">Tipo de documento</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {DOC_TYPES.map(type => (
                <button key={type.id}
                  onClick={() => { setSelectedType(type); setFields({}); setGeneratedDoc(''); setShowPreview(false); }}
                  className={`p-3 rounded-xl text-left transition-all hover:scale-[1.02] border ${selectedType?.id === type.id ? 'bg-red-500/15 border-red-500/30' : 'bg-white/5 hover:bg-white/10 border-white/5'}`}>
                  <span className="text-xl block mb-1">{type.emoji}</span>
                  <p className="text-white text-xs font-medium leading-tight">{type.label}</p>
                  <p className="text-gray-500 text-xs mt-0.5 hidden sm:block">{type.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Fields form */}
          {selectedType && (
            <div className="glass-panel p-5 rounded-2xl animate-fade-in-up">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">{selectedType.emoji}</span>
                <div>
                  <h3 className="text-white font-semibold">{selectedType.label}</h3>
                  <p className="text-gray-500 text-xs">{selectedType.description}</p>
                </div>
              </div>
              <div className="space-y-3 mb-5">
                {selectedType.fields.map(fieldId => (
                  <div key={fieldId}>
                    <label className="text-gray-400 text-xs mb-1 block">{FIELD_LABELS[fieldId] || fieldId}</label>
                    <div className="flex gap-2">
                      {MULTILINE_FIELDS.includes(fieldId) ? (
                        <textarea
                          value={fields[fieldId] || ''}
                          onChange={e => setFields(p => ({ ...p, [fieldId]: e.target.value }))}
                          placeholder={`${FIELD_LABELS[fieldId] || fieldId}...`}
                          className="input-glass flex-1 text-sm resize-none" rows={3}
                        />
                      ) : (
                        <input
                          value={fields[fieldId] || ''}
                          onChange={e => setFields(p => ({ ...p, [fieldId]: e.target.value }))}
                          placeholder={`${FIELD_LABELS[fieldId] || fieldId}...`}
                          className="input-glass flex-1 text-sm"
                        />
                      )}
                      <button onClick={() => handleVoice(fieldId)}
                        className={`px-3 rounded-xl flex-shrink-0 transition-colors ${listening === fieldId ? 'bg-red-500 text-white animate-pulse' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
                        {listening === fieldId ? <MicOff size={16} /> : <Mic size={16} />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={generateDocument} disabled={generating}
                className="btn-primary w-full flex items-center justify-center gap-2 py-3">
                {generating ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Generando...</>
                ) : (
                  <><Sparkles size={18} />Generar {selectedType.label}</>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {generatedDoc && showPreview && (
            <div className="glass-panel p-4 rounded-2xl animate-fade-in-up">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                  <Eye size={16} className="text-orange-400" />Vista previa
                </h3>
                <div className="flex gap-1">
                  <button onClick={copyDoc} className="p-1.5 hover:bg-white/10 rounded-lg">
                    {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} className="text-gray-400" />}
                  </button>
                  <button onClick={downloadDoc} className="p-1.5 hover:bg-white/10 rounded-lg">
                    <Download size={14} className="text-gray-400" />
                  </button>
                  <button onClick={() => setShowPreview(false)} className="p-1.5 hover:bg-white/10 rounded-lg">
                    <X size={14} className="text-gray-400" />
                  </button>
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-3 max-h-72 overflow-y-auto custom-scrollbar">
                <pre className="text-gray-300 text-xs whitespace-pre-wrap leading-relaxed font-sans">{generatedDoc}</pre>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={copyDoc} className="btn-secondary text-xs flex-1 flex items-center justify-center gap-1.5">
                  {copied ? <Check size={12} /> : <Copy size={12} />}{copied ? 'Copiado' : 'Copiar'}
                </button>
                <button onClick={downloadDoc} className="btn-primary text-xs flex-1 flex items-center justify-center gap-1.5">
                  <Download size={12} />Descargar
                </button>
              </div>
            </div>
          )}

          {history.length > 0 && (
            <div className="glass-panel p-4 rounded-2xl">
              <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                <Clock size={14} className="text-gray-400" />Historial
              </h3>
              <div className="space-y-2 max-h-60 overflow-y-auto custom-scrollbar">
                {history.map((h, i) => (
                  <button key={i}
                    onClick={() => { setGeneratedDoc(h.doc); setShowPreview(true); setSelectedType(DOC_TYPES.find(t => t.label === h.type) || null); setFields(h.fields); }}
                    className="w-full text-left p-2.5 rounded-xl hover:bg-white/5 transition-colors flex items-center gap-2">
                    <span className="text-lg">{h.emoji}</span>
                    <div className="min-w-0">
                      <p className="text-white text-xs font-medium truncate">{h.type}</p>
                      <p className="text-gray-600 text-xs">{new Date(h.date).toLocaleDateString('es-MX')}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {!selectedType && history.length === 0 && (
            <div className="glass-panel p-8 rounded-2xl text-center">
              <FileText size={40} className="text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Selecciona un tipo de documento para comenzar</p>
            </div>
          )}
        </div>
      </div>

      <AIAgentModule sectionContext="documentos profesionales" compact={true} placeholder="Ayúdame a crear un documento..." />
    </div>
  
      </>
    );
};

export default DocumentsModule;
